import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { utcToTimezone } from 'helpers/dates';
import { useApp } from 'providers/AppProvider';
import {
  useDashboardSubmissions,
  useDashboardDataTablePagination,
  useDashboardDataTableSortSearch,
} from '../hooks';
import { buildCsv, exportCsv } from '../utils';
import DashboardDataTable from '../DashboardDataTable';
import DashboardDataTableLinkCell from '../DashboardDataTable/DashboardDataTableLinkCell';

const DashboardMissed = () => {
  const { t } = useTranslation();
  const { timezone } = useApp();
  const { missed } = useDashboardSubmissions();
  const { paginationControls, paginateRows } = useDashboardDataTablePagination({ data: missed });
  const { executeSearchSort } = useDashboardDataTableSortSearch({
    searchableColumns: ['referenceNumber', 'organizationName', 'projectName'],
  });

  const columns = useMemo(
    () => [
      {
        id: 'referenceNumber',
        label: t('dashboard.data_table.ref_num'),
        isSortable: true,
        render: 'string',
      },
      {
        id: 'projectName',
        label: t('dashboard.data_table.project_name'),
        isSortable: true,
        render: 'string',
      },
      {
        id: 'organizationName',
        label: t('dashboard.data_table.organization_name'),
        isSortable: true,
        render: 'string',
      },
      {
        id: 'dateClose',
        label: t('dashboard.data_table.date_project_close'),
        isSortable: true,
        render: row =>
          row.dateClose
            ? utcToTimezone(row.dateClose, { timezone, format: 'DD MMM YYYY, h:mma z' })
            : '-',
      },
    ],
    [t, timezone]
  );

  const rowGetter = useCallback(
    ({ search, sortBy }) => {
      const rows = executeSearchSort({ rows: missed, search, sortBy });
      return paginateRows(rows);
    },
    [missed, executeSearchSort, paginateRows]
  );

  const fileGetter = useCallback(
    ({ search, sort: sortBy }) => {
      const rows = executeSearchSort({ rows: missed, search, sortBy });
      const csv = buildCsv(rows, columns);
      exportCsv(csv, {
        fileName: t('dashboard.missed.data_table.export_file_name'),
        appendDate: true,
      });
    },
    [t, executeSearchSort, missed, columns]
  );

  return (
    <>
      <DashboardDataTable
        count={missed.length}
        title={t('dashboard.missed.title')}
        DataTableProps={{
          fileGetter,
          rowGetter,
          columns,
          linkRenderer: row => <DashboardDataTableLinkCell row={row} type="missed" />,
          caption: t('dashboard.missed.data_table.caption'),
        }}
      />
      <DashboardDataTable.Pagination
        PaginationControlsProps={{
          ...paginationControls.props,
          page: paginationControls.state.page,
          totalItems: paginationControls.state.total,
        }}
      />
    </>
  );
};

export default DashboardMissed;
